import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  acceptAllConsent,
  getStoredConsent,
  saveConsent,
} from '../../lib/consent'
import { ANALYTICS_EVENTS, track } from '../../lib/analytics'

type Mode = 'summary' | 'customize'

type ToggleProps = {
  label: string
  description: string
  checked: boolean
  disabled?: boolean
  onChange?: (next: boolean) => void
}

const Toggle = ({ label, description, checked, disabled, onChange }: ToggleProps) => {
  return (
    <div className='flex items-start justify-between gap-4 py-3'>
      <div className='flex flex-col gap-1'>
        <span className='font-inter text-sm font-medium text-gray-900'>{label}</span>
        <span className='font-inter text-xs font-light leading-relaxed text-gray-600'>
          {description}
        </span>
      </div>
      <button
        type='button'
        role='switch'
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        className={`relative shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-orange-600' : 'bg-gray-300'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  )
}

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false)
  const [mode, setMode] = useState<Mode>('summary')
  const [analytics, setAnalytics] = useState(true)
  const [advertising, setAdvertising] = useState(true)

  useEffect(() => {
    if (getStoredConsent() === null) setVisible(true)
  }, [])

  if (!visible) return null

  const handleAcceptAll = () => {
    acceptAllConsent()
    track(ANALYTICS_EVENTS.CONSENT_ACCEPT_ALL_CLICKED)
    setVisible(false)
  }

  const handleSave = () => {
    saveConsent({ analytics, advertising })
    track(ANALYTICS_EVENTS.CONSENT_PREFERENCES_SAVED, { analytics, advertising })
    setVisible(false)
  }

  return (
    <div
      role='dialog'
      aria-label='Cookie consent'
      className='fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-50 bg-white border border-gray-200 rounded-2xl shadow-lg p-5 md:p-6'
    >
      {mode === 'summary' ? (
        <>
          <h3 className='font-inter text-lg font-medium tracking-tight text-gray-900 mb-2'>
            Your privacy choices
          </h3>
          <p className='font-inter text-sm font-light leading-relaxed text-gray-700 mb-4'>
            We use cookies and similar technologies — including Google Analytics with
            enhanced user insights and audience list sharing — to understand how
            visitors use our site and improve your experience. Essential cookies are
            always on. Read our{' '}
            <Link to='/privacy-policy' className='underline text-orange-600'>
              Privacy Policy
            </Link>
            .
          </p>
          <div className='flex flex-col sm:flex-row gap-2'>
            <button
              onClick={() => {
                track(ANALYTICS_EVENTS.CONSENT_CUSTOMIZE_OPENED)
                setMode('customize')
              }}
              className='flex-1 py-2 rounded-full border border-gray-300 text-gray-900 text-sm font-inter hover:bg-gray-100 transition-colors'
            >
              Customize preferences
            </button>
            <button
              onClick={handleAcceptAll}
              className='flex-1 py-2 rounded-full bg-orange-600 text-white text-sm font-inter hover:opacity-80 transition-colors'
            >
              Accept all
            </button>
          </div>
        </>
      ) : (
        <>
          <h3 className='font-inter text-lg font-medium tracking-tight text-gray-900 mb-1'>
            Customize preferences
          </h3>
          <p className='font-inter text-xs font-light leading-relaxed text-gray-600 mb-2'>
            Choose which categories of cookies you're happy for us to use. You can
            change this anytime.
          </p>
          <div className='divide-y divide-gray-100 border-t border-b border-gray-100 mb-4'>
            <Toggle
              label='Essential'
              description='Required for the site to function (e.g., remembering this preference). Always on.'
              checked
              disabled
            />
            <Toggle
              label='Analytics & performance'
              description='Google Analytics and Mixpanel help us measure site usage and improve the experience.'
              checked={analytics}
              onChange={setAnalytics}
            />
            <Toggle
              label='Audience insights & advertising'
              description='Enables Google Signals, enhanced user insights, and audience list sharing for remarketing and campaign measurement.'
              checked={advertising}
              onChange={setAdvertising}
            />
          </div>
          <div className='flex flex-col sm:flex-row gap-2'>
            <button
              onClick={() => setMode('summary')}
              className='flex-1 py-2 rounded-full border border-gray-300 text-gray-900 text-sm font-inter hover:bg-gray-100 transition-colors'
            >
              Back
            </button>
            <button
              onClick={handleSave}
              className='flex-1 py-2 rounded-full bg-orange-600 text-white text-sm font-inter hover:opacity-80 transition-colors'
            >
              Save preferences
            </button>
          </div>
        </>
      )}
    </div>
  )
}
